'use client'

import { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { clearPagesCache } from '@/shared/lib/pwaCache'
import { resolveClientSiteId } from '@/shared/lib/clientSites'

// Set before the deliberate post-update reload so a bad deploy (or an HTTP
// cache that keeps serving the old bundle) degrades to the toast instead of
// a reload loop.
const RELOAD_GUARD_KEY = 'app-update-reloaded-for'
const CHECK_THROTTLE_MS = 60_000

// Drop the service worker's cached pages, then reload. The SW serves
// navigations stale-while-revalidate, so a plain reload would repaint the
// same old bundle from cache; flushing first guarantees the next navigation
// hits the network. Only called right after a successful version fetch, so
// we know we're online.
async function reloadOntoNewVersion(serverBuildId: string) {
  try {
    await clearPagesCache()
  } catch {
    // The SW's own background revalidation has likely refreshed the cache by
    // now anyway — reload regardless.
  }
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, serverBuildId)
  } catch {}
  window.location.reload()
}

// Text-like inputs whose value counts as "unsaved work" — a reload would
// discard it. Checkboxes/selects/dates always carry a value, so they're out.
const TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
  'email',
  'password',
  'number',
  'tel',
  'url',
])

function hasUnsavedInput(): boolean {
  const focused = document.activeElement
  if (
    focused instanceof HTMLInputElement ||
    focused instanceof HTMLTextAreaElement ||
    (focused instanceof HTMLElement && focused.isContentEditable)
  ) {
    return true
  }
  return Array.from(document.querySelectorAll('input, textarea')).some(
    (el) =>
      (el instanceof HTMLTextAreaElement ||
        (el instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(el.type))) &&
      el.value.trim() !== ''
  )
}

/**
 * Registers the PWA service worker in production (skipped in development so
 * it never interferes with Next.js hot reloading), and keeps installed PWAs
 * from running a stale deploy: the SW serves pages stale-while-revalidate,
 * so the first launch after a deploy paints the previous bundle. On launch
 * and whenever the app returns to the foreground this compares the running
 * bundle's build id against /api/app-version — on a mismatch it silently
 * flushes the page cache and reloads, deferring while the user has typed-in
 * input a reload would discard. The "new version" toast only appears in the
 * degenerate case where that reload didn't actually deliver the new bundle
 * (CDN lag, stubborn HTTP cache), as a manual escape hatch instead of a
 * reload loop.
 */
export function ServiceWorkerRegistrar() {
  // Server build id behind a pending update; null = no toast.
  const [pendingBuildId, setPendingBuildId] = useState<string | null>(null)

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return
    if (!('serviceWorker' in navigator)) return
    // Never install the family-apps PWA service worker on a client site's
    // custom domain — it would cache their marketing page under our
    // stale-while-revalidate rules and serve our offline shell.
    if (resolveClientSiteId(window.location.host)) return

    const register = () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Registration failures are non-fatal — the app still works online.
      })
    }

    let lastCheck = 0
    let reloading = false

    const checkForUpdate = async () => {
      const clientBuildId = process.env.NEXT_PUBLIC_BUILD_ID
      if (!clientBuildId || clientBuildId === 'dev' || reloading) return
      const now = Date.now()
      if (now - lastCheck < CHECK_THROTTLE_MS) return
      lastCheck = now

      let serverBuildId: string | null = null
      try {
        const res = await fetch('/api/app-version', { cache: 'no-store' })
        if (!res.ok) return
        serverBuildId = (await res.json()).buildId
      } catch {
        return // Offline — the cached bundle is the only option anyway.
      }
      if (!serverBuildId || serverBuildId === clientBuildId) return

      if (sessionStorage.getItem(RELOAD_GUARD_KEY) === serverBuildId) {
        // We already reloaded for this deploy and still got the old bundle:
        // don't loop — fall back to a manual nudge.
        setPendingBuildId(serverBuildId)
        return
      }
      // Mid-edit: skip for now; a later launch/foreground check retries.
      if (hasUnsavedInput()) return

      reloading = true
      void reloadOntoNewVersion(serverBuildId)
    }

    void checkForUpdate()

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void checkForUpdate()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    if (document.readyState === 'complete') {
      register()
    } else {
      window.addEventListener('load', register)
    }
    return () => {
      window.removeEventListener('load', register)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  if (!pendingBuildId) return null

  return (
    <div
      role="status"
      className="fixed z-50 left-4 right-4 bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] sm:left-auto sm:right-6 sm:w-80 lg:bottom-6"
    >
      <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50">
          <RefreshCw className="h-4 w-4 text-blue-600" />
        </div>
        <p className="min-w-0 flex-1 text-sm text-gray-700">
          A new version is available.
        </p>
        <button
          type="button"
          onClick={() => void reloadOntoNewVersion(pendingBuildId)}
          className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          Refresh
        </button>
        <button
          type="button"
          onClick={() => setPendingBuildId(null)}
          aria-label="Dismiss"
          className="-mr-1 shrink-0 p-1 text-gray-400 transition-colors hover:text-gray-600"
        >
          <span aria-hidden>×</span>
        </button>
      </div>
    </div>
  )
}
